import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, CircularProgress, IconButton, Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import CancelIcon from "@material-ui/icons/Cancel";
import BarChartIcon from "@material-ui/icons/BarChart";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import theme from "../../theme";
import { isSuper } from "../../lib/permissions";
import {
  engCampaignListAction, engCampaignCreateAction,
  engCampaignLaunchAction, engCampaignPauseAction, engCampaignCancelAction,
  engCampaignAnalyticsAction, engResetMutation,
  engSegmentListAction, engTemplateListAction,
} from "../../containers/engagement/action";
import CampaignAnalytics from "./CampaignAnalytics";
import { tokens, STATUS_STYLE, toMessages, SUCCESS_MESSAGES, ERROR_FALLBACKS } from "./utils";

const MUTATION_KINDS = ["campaign_create", "campaign_launch", "campaign_pause", "campaign_cancel"];

const useStyles = makeStyles(() => ({
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
  title: { fontSize: 20, fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.DodgerBlue },
  createBtn: {
    backgroundColor: theme.colors.DodgerBlue,
    color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    "&:hover": { backgroundColor: theme.colors.DodgerBlue, opacity: 0.88 },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: tokens.surface,
    borderRadius: 10,
    overflow: "hidden",
    border: `1px solid ${tokens.border}`,
  },
  th: {
    padding: "12px 16px",
    textAlign: "left",
    fontSize: 12,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
    background: tokens.tint,
    borderBottom: `1px solid ${tokens.border}`,
  },
  td: {
    padding: "12px 16px",
    fontSize: 13,
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPRO,
    borderBottom: `1px solid ${tokens.borderSoft}`,
  },
  tdName: { fontFamily: theme.fontFamily.MARKPROMEDIUM },
  statusChip: { fontFamily: theme.fontFamily.MARKPROMEDIUM, fontSize: 11 },
  actions: { display: "flex", gap: 4 },
  empty: { textAlign: "center", color: tokens.textSubtle, padding: "48px 0", fontFamily: theme.fontFamily.MARKPRO },
  spinnerRow: { display: "flex", justifyContent: "center", padding: "32px 0" },
  dialogTitle: { color: theme.colors.DodgerBlue, fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 18, padding: "16px 24px" },
}));

export default function CampaignList() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    campaigns, segments, templates, loading,
    mutationLoading, mutationSuccess, mutationError, lastMutation,
  } = useSelector((s) => s.engagementReducer);

  // Engagement writes (create / launch / pause / cancel) are super-only;
  // engagement.view users see the module read-only.
  const me = useSelector((s) => s.loginReducer?.me);
  let _storedUser = null;
  try {
    _storedUser = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    _storedUser = null;
  }
  const canManage = isSuper(me || _storedUser);

  const [open, setOpen] = useState(false);
  const [analyticsId, setAnalyticsId] = useState(null);
  const [form, setForm] = useState({ name: "", segment: "", template: "", send_mode: "SCHEDULED", send_at: "" });

  useEffect(() => {
    dispatch(engCampaignListAction({}));
    // The New Campaign dialog's Segment/Template dropdowns are sourced from
    // shared reducer state — prefetch them so the dialog isn't blank when the
    // user opens it without first visiting the other tabs.
    if (!segments.length) dispatch(engSegmentListAction({}));
    if (!templates.length) dispatch(engTemplateListAction({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Success toast — only fire for this screen's mutations.
  useEffect(() => {
    if (mutationSuccess && MUTATION_KINDS.includes(lastMutation)) {
      enqueueSnackbar(SUCCESS_MESSAGES[lastMutation], { variant: "success" });
      if (lastMutation === "campaign_create") {
        setOpen(false);
        setForm({ name: "", segment: "", template: "", send_mode: "SCHEDULED", send_at: "" });
      }
      dispatch(engResetMutation());
    }
  }, [mutationSuccess, lastMutation, dispatch, enqueueSnackbar]);

  useEffect(() => {
    if (mutationError && MUTATION_KINDS.includes(lastMutation)) {
      toMessages(mutationError, ERROR_FALLBACKS[lastMutation]).forEach((m) =>
        enqueueSnackbar(m, { variant: "error" })
      );
      dispatch(engResetMutation());
    }
  }, [mutationError, lastMutation, dispatch, enqueueSnackbar]);

  const handleCreate = () => {
    const payload = { ...form };
    if (!payload.send_at) delete payload.send_at;
    dispatch(engCampaignCreateAction(payload));
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString() : "—";

  return (
    <div>
      <div className={classes.header}>
        <span className={classes.title}>Campaigns</span>
        {canManage ? (
          <Button variant="contained" className={classes.createBtn} startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            New Campaign
          </Button>
        ) : null}
      </div>

      {analyticsId && (
        <CampaignAnalytics campaignId={analyticsId} onClose={() => setAnalyticsId(null)} />
      )}

      {!analyticsId && loading && campaigns.length === 0 && (
        <div className={classes.spinnerRow}>
          <CircularProgress size={28} style={{ color: theme.colors.DodgerBlue }} />
        </div>
      )}

      {!analyticsId && (
        <>
          {!loading && campaigns.length === 0 ? (
            <div className={classes.empty}>No campaigns yet.</div>
          ) : campaigns.length > 0 && (
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>Name</th>
                  <th className={classes.th}>Status</th>
                  <th className={classes.th}>Mode</th>
                  <th className={classes.th}>Send At</th>
                  <th className={classes.th}>A/B</th>
                  <th className={classes.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => {
                  const st = STATUS_STYLE[c.status] || STATUS_STYLE.DRAFT;
                  return (
                    <tr key={c.id}>
                      <td className={`${classes.td} ${classes.tdName}`}>{c.name}</td>
                      <td className={classes.td}>
                        <Chip label={c.status} size="small" className={classes.statusChip} style={{ background: st.bg, color: st.color }} />
                      </td>
                      <td className={classes.td}>{c.send_mode}</td>
                      <td className={classes.td}>{fmtDate(c.send_at)}</td>
                      <td className={classes.td}>{c.is_ab_test ? "Yes" : "No"}</td>
                      <td className={classes.td}>
                        <div className={classes.actions}>
                          {canManage && c.status === "DRAFT" && (
                            <Tooltip title="Launch">
                              <IconButton size="small" onClick={() => dispatch(engCampaignLaunchAction(c.id))} disabled={mutationLoading}>
                                <PlayArrowIcon fontSize="small" style={{ color: theme.colors.ApproveGreen }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canManage && ["SCHEDULED", "RUNNING"].includes(c.status) && (
                            <Tooltip title="Pause">
                              <IconButton size="small" onClick={() => dispatch(engCampaignPauseAction(c.id))} disabled={mutationLoading}>
                                <PauseIcon fontSize="small" style={{ color: "#B7791F" }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canManage && !["COMPLETED", "CANCELLED"].includes(c.status) && (
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={() => dispatch(engCampaignCancelAction(c.id))} disabled={mutationLoading}>
                                <CancelIcon fontSize="small" style={{ color: theme.colors.RejectRed }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Analytics">
                            <IconButton size="small" onClick={() => { setAnalyticsId(c.id); dispatch(engCampaignAnalyticsAction(c.id)); }}>
                              <BarChartIcon fontSize="small" style={{ color: theme.colors.DodgerBlue }} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography className={classes.dialogTitle}>New Campaign</DialogTitle>
        <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
          <TextField label="Campaign Name" fullWidth variant="outlined" size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Segment</InputLabel>
            <Select value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} label="Segment">
              {segments.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Template</InputLabel>
            <Select value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} label="Template">
              {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Send Mode</InputLabel>
            <Select value={form.send_mode} onChange={(e) => setForm({ ...form, send_mode: e.target.value })} label="Send Mode">
              <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              <MenuItem value="TRIGGERED">Triggered</MenuItem>
            </Select>
          </FormControl>
          {form.send_mode === "SCHEDULED" && (
            <TextField
              label="Send At"
              type="datetime-local"
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.send_at}
              onChange={(e) => setForm({ ...form, send_at: e.target.value })}
            />
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setOpen(false)} style={{ color: tokens.textMuted }}>Cancel</Button>
          <Button
            variant="contained"
            className={classes.createBtn}
            onClick={handleCreate}
            disabled={!form.name || !form.segment || !form.template || mutationLoading}
          >
            {mutationLoading && lastMutation === "campaign_create"
              ? <CircularProgress size={18} style={{ color: theme.colors.White }} />
              : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
